import { c as _c } from "react/compiler-runtime";
import { IcoClock40 } from '@onefootprint/icons';
import { handleFileUpload } from '@onefootprint/idv2';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { Button } from '@onefootprint/ui';
import upperFirst from 'lodash/upperFirst';
import type React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryName } from '@/idv/hooks/use-country-name';
import { getLogger } from '@/idv/utils';
import { HeaderTitle } from '../../../../components';
import { DESKTOP_INTERACTION_BOX_HEIGHT } from '../../constants';
import useProcessImage, { ImageFileOrUrlProcessingError, isProcessingResultError } from '../../hooks/use-process-image';
import { type IdDocImageErrorType, IdDocImageUploadError, type ReceivedImagePayload } from '../../types';
import DesktopHeader from '../desktop-header/desktop-header';
import ErrorComponent from '../error';
import Loading from '../loading';
import DraggableInputField from './components/draggable-input-field';
const {
  logWarn
} = getLogger({
  location: 'desktop-photo-prompt'
});
type DesktopPhotoPromptProps = {
  docName?: string;
  sideName?: string;
  country?: Iso3166TwoDigitCountryCode;
  isRetry?: boolean;
  errors?: IdDocImageErrorType[];
  hasBadConnectivity?: boolean;
  allowPdf: boolean;
  onUploadSuccess: (arg: ReceivedImagePayload) => void;
  onUploadError: (errs: IdDocImageUploadError[]) => void;
  showCameraFallbackText?: boolean;
  isSelfie?: boolean;
};
const DesktopPhotoPrompt = t0 => {
  const $ = _c(78);
  const {
    docName,
    sideName,
    country,
    isRetry,
    errors,
    hasBadConnectivity,
    allowPdf,
    onUploadSuccess,
    onUploadError,
    showCameraFallbackText,
    isSelfie
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.components.desktop-photo-prompt"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t1);
  const uploadPhotoRef = useRef(undefined);
  let t2;
  if ($[1] !== allowPdf) {
    t2 = {
      allowPdf
    };
    $[1] = allowPdf;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    processImageFile,
    acceptedFileFormats
  } = useProcessImage(t2);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsLoading(false);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onProcessingDone = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setHasError(true);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleError = t4;
  let t5;
  if ($[5] !== hasBadConnectivity || $[6] !== onUploadError || $[7] !== onUploadSuccess || $[8] !== processImageFile || $[9] !== showCameraFallbackText) {
    t5 = async file => {
      setIsLoading(true);
      setHasError(false);
      const processResult = await processImageFile(file, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        const errorType = processResult.error;
        onProcessingDone();
        onUploadError([errorType === ImageFileOrUrlProcessingError.heicNotSupported ? IdDocImageUploadError.heicNotSupported : IdDocImageUploadError.unknownUploadError]);
        logWarn("The uploaded image could not be processed due to an unknown error.");
        return;
      }
      onUploadSuccess({
        imageFile: processResult.file,
        captureKind: "upload",
        extraCompressed: processResult.extraCompressed,
        forcedUpload: !!showCameraFallbackText
      });
      onProcessingDone();
    };
    $[5] = hasBadConnectivity;
    $[6] = onUploadError;
    $[7] = onUploadSuccess;
    $[8] = processImageFile;
    $[9] = showCameraFallbackText;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleImage = t5;
  let t6;
  if ($[11] !== acceptedFileFormats || $[12] !== handleImage || $[13] !== onUploadError) {
    t6 = ev => {
      const {
        files
      } = ev.target;
      const result = handleFileUpload({
        acceptedFileFormats,
        files
      });
      if (!result.ok) {
        onUploadError(result.errors);
        return;
      }
      handleImage(result.file);
    };
    $[11] = acceptedFileFormats;
    $[12] = handleImage;
    $[13] = onUploadError;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleImageUpload = t6;
  const getCountryName = useCountryName();
  let t7;
  if ($[15] !== country || $[16] !== getCountryName) {
    t7 = getCountryName(country);
    $[15] = country;
    $[16] = getCountryName;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const countryName = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      uploadPhotoRef.current?.click();
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleUpload = t8;
  const onFileInputClick = _temp;
  let acceptedFormatsForHint;
  if ($[19] !== acceptedFileFormats) {
    acceptedFormatsForHint = ["JPG", "PNG", "WEBP", "BMP"];
    if (acceptedFileFormats.includes("pdf") || acceptedFileFormats.includes("application/pdf")) {
      acceptedFormatsForHint.push("PDF");
    }
    $[19] = acceptedFileFormats;
    $[20] = acceptedFormatsForHint;
  } else {
    acceptedFormatsForHint = $[20];
  }
  let t9;
  if ($[21] !== docName) {
    t9 = docName ? upperFirst(docName) : docName;
    $[21] = docName;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== sideName) {
    t10 = sideName ? upperFirst(sideName) : sideName;
    $[23] = sideName;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== country || $[26] !== isSelfie || $[27] !== t10 || $[28] !== t9) {
    t11 = <DesktopHeader docName={t9} country={country} sideName={t10} isSelfie={isSelfie} />;
    $[25] = country;
    $[26] = isSelfie;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== isLoading || $[31] !== showCameraFallbackText || $[32] !== t) {
    t12 = showCameraFallbackText && !isLoading && <><HeaderTitle title={t(_temp2)} display="flex" flexDirection="column" icon={IcoClock40} /><div className="text-body-2 text-quaternary">{t(_temp3)}</div></>;
    $[30] = isLoading;
    $[31] = showCameraFallbackText;
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== country || $[35] !== countryName || $[36] !== docName || $[37] !== errors || $[38] !== isRetry || $[39] !== sideName) {
    t13 = isRetry && <div className="p-6"><ErrorComponent errors={errors ?? [{
        errorType: "unknown_error"
      }]} sideName={sideName} docName={docName} countryName={countryName ?? country} /></div>;
    $[34] = country;
    $[35] = countryName;
    $[36] = docName;
    $[37] = errors;
    $[38] = isRetry;
    $[39] = sideName;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== isLoading || $[42] !== isRetry) {
    t14 = !isRetry && isLoading && <Loading step="process" />;
    $[41] = isLoading;
    $[42] = isRetry;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== acceptedFormatsForHint || $[45] !== allowPdf || $[46] !== isLoading || $[47] !== isRetry || $[48] !== t) {
    t15 = !isRetry && !isLoading && <><div className="flex"><div className="text-label-2 text-accent">{t(_temp4)}</div><div className="text-body-2 text-quaternary"> {t(_temp5)}</div></div>{!allowPdf && <div className="text-body-2 text-quaternary">{t(_temp6, {
          accepted: acceptedFormatsForHint.join(", ")
        })}</div>}</>;
    $[44] = acceptedFormatsForHint;
    $[45] = allowPdf;
    $[46] = isLoading;
    $[47] = isRetry;
    $[48] = t;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== acceptedFileFormats) {
    t16 = acceptedFileFormats.join(",");
    $[50] = acceptedFileFormats;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== handleImageUpload || $[53] !== t16) {
    t17 = <input ref={uploadPhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept={t16} onClick={onFileInputClick} onChange={handleImageUpload} className="hidden" />;
    $[52] = handleImageUpload;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== allowPdf || $[56] !== handleImage || $[57] !== hasError || $[58] !== isLoading || $[59] !== onUploadError || $[60] !== t12 || $[61] !== t13 || $[62] !== t14 || $[63] !== t15 || $[64] !== t17) {
    t18 = <DraggableInputField height={DESKTOP_INTERACTION_BOX_HEIGHT} onComplete={handleImage} isLoading={isLoading} hasError={hasError} onUploadError={onUploadError} allowPdf={allowPdf}>{t12}{t13}{t14}{t15}{t17}</DraggableInputField>;
    $[55] = allowPdf;
    $[56] = handleImage;
    $[57] = hasError;
    $[58] = isLoading;
    $[59] = onUploadError;
    $[60] = t12;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = t17;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  const t19 = isRetry ? handleUpload : handleError;
  const t20 = `doc:${isRetry ? "upload-photo-retry" : "continue"}`;
  let t21;
  if ($[66] !== isRetry || $[67] !== t) {
    t21 = isRetry ? t(_temp7) : t(_temp8);
    $[66] = isRetry;
    $[67] = t;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== isLoading || $[70] !== t19 || $[71] !== t20 || $[72] !== t21) {
    t22 = <Button fullWidth={true} disabled={isLoading} onClick={t19} size="large" data-dd-action-name={t20}>{t21}</Button>;
    $[69] = isLoading;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== t11 || $[75] !== t18 || $[76] !== t22) {
    t23 = <div className="flex flex-col gap-6 max-md:px-2">{t11}{t18}{t22}</div>;
    $[74] = t11;
    $[75] = t18;
    $[76] = t22;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  return t23;
};
export default DesktopPhotoPrompt;
function _temp(ev_0) {
  ev_0.stopPropagation();
}
function _temp2($) {
  return $["camera-fallback-text"].title;
}
function _temp3($_0) {
  return $_0["camera-fallback-text"].subtitle;
}
function _temp4($_1) {
  return $_1["upload-link-button"].title;
}
function _temp5($_2) {
  return $_2["drag-drop-text"]["line-1"];
}
function _temp6($_3) {
  return $_3["drag-drop-text"]["line-2"];
}
function _temp7($_4) {
  return $_4["choose-different-file"];
}
function _temp8($_5) {
  return $_5.continue;
}